/**
 * Service for synchronizing Google Calendar events with the local Firestore cache.
 * Implements throttling to respect API limits and retention to manage storage.
 */
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { gcalAuthService } from '@/features/planning/api/gcalAuthService';
import { GCAL_SYNC_THROTTLE_MS, GCAL_RETENTION_DAYS } from '@/config/constants';

/**
 * Service for synchronizing Google Calendar events with the local Firestore cache.
 * Implements throttling to respect API limits and retention to manage storage.
 */
class GCalSyncService {
  /**
   * Synchronizes remote Google Calendar events to local Firestore cache.
   *
   * @param {string} userId - The unique identifier of the user.
   * @returns {Promise<Array>} The list of synchronized (cached) events.
   */
  async syncGCalEvents(userId) {
    if (!userId)
      throw new Error('User ID is required for Google Calendar sync');

    // 1. Check throttling to avoid redundant API calls
    const throttled = await this._isThrottled(userId);
    if (throttled) {
      return this.getCachedEvents(userId);
    }

    try {
      // 2. Fetch from Google Calendar primary calendar
      const token = await gcalAuthService.getToken();
      const timeMin = new Date();
      timeMin.setDate(timeMin.getDate() - 1);
      timeMin.setHours(0, 0, 0, 0);
      const timeMax = new Date();
      timeMax.setDate(timeMax.getDate() + GCAL_RETENTION_DAYS);
      timeMax.setHours(23, 59, 59, 999);

      const params = new URLSearchParams({
        singleEvents: 'true',
        orderBy: 'startTime',
        timeMin: timeMin.toISOString(),
        timeMax: timeMax.toISOString(),
        maxResults: '250',
      });
      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.ok) {
        throw new Error(`Google API responded with status ${response.status}`);
      }

      const data = await response.json();
      const remoteEvents = data.items || [];

      // 3. Update Firestore Cache with normalized events
      await this._updateCache(userId, remoteEvents);

      // 4. Update sync status (last sync time)
      await this._updateSyncStatus(userId);

      // 5. Apply retention (cleanup events outside the window)
      await this._applyRetention(userId);

      return this.getCachedEvents(userId);
    } catch (error) {
      console.error('[GCalSyncService] Sync failed:', error);
      // Fallback to cache on error to ensure UI stability
      return this.getCachedEvents(userId);
    }
  }

  /**
   * Retrieves all currently cached Google Calendar events from Firestore.
   */
  async forceSyncGCalEvents(userId) {
    const statusRef = doc(db, `users/${userId}/preferences/settings`);
    await setDoc(statusRef, { gcalCache: { lastSync: null } }, { merge: true });
    return this.syncGCalEvents(userId);
  }

  async getCachedEvents(userId) {
    try {
      const snap = await getDoc(
        doc(db, `users/${userId}/preferences/settings`),
      );
      return snap.exists() ? snap.data().gcalCache?.events || [] : [];
    } catch (error) {
      console.error('[GCalSyncService] Failed to get cached events:', error);
      throw error;
    }
  }

  /**
   * Checks if a sync attempt should be blocked based on the configured throttle interval.
   */
  async _isThrottled(userId) {
    try {
      const statusRef = doc(db, `users/${userId}/preferences/settings`);
      const snap = await getDoc(statusRef);

      if (!snap.exists()) return false;

      const lastSyncAt = snap.data().gcalCache?.lastSync?.toMillis() || 0;
      const elapsed = Date.now() - lastSyncAt;

      return elapsed < GCAL_SYNC_THROTTLE_MS;
    } catch (error) {
      console.error('[GCalSyncService] Throttling check failed:', error);
      return false; // Better to allow sync than block it indefinitely on error
    }
  }

  /**
   * Bulk updates the Firestore cache with normalized remote events.
   */
  async _updateCache(userId, remoteEvents) {
    if (remoteEvents.length === 0) return;

    try {
      const prefDocRef = doc(db, `users/${userId}/preferences/settings`);

      // We store the events directly in the settings document for simplicity in this version,
      // or we can keep them in the subcollection. To match subscribeToGCalData expectations:
      await setDoc(
        prefDocRef,
        {
          gcalCache: {
            events: remoteEvents.filter((event) => event.start),
            lastSync: serverTimestamp(),
          },
        },
        { merge: true },
      );
    } catch (error) {
      console.error('[GCalSyncService] Cache update failed:', error);
      throw error;
    }
  }

  /**
   * Updates the sync status document (now merged into settings).
   */
  async _updateSyncStatus(_userId) {
    // This is now handled within _updateCache for consistency
    return Promise.resolve();
  }

  /**
   * Removes cached events that are older than the retention threshold.
   */
  async _applyRetention(_userId) {
    // With events stored in the document array, retention is managed by
    // filtering the array before saving in _updateCache if needed.
    // For now, we skip individual document deletion as they are merged.
    return Promise.resolve();
  }
}

export const gcalSyncService = new GCalSyncService();
